import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Input, Spin } from "antd";
import { CommunicationHttpService } from "../../services/Http/CommunicationHttpService";

// ── Segment types ─────────────────────────────────────────────────────────────

type TextSegment = { kind: "text"; value: string };
type ImageSegment = { kind: "image"; id: number };
type Segment = TextSegment | ImageSegment;

// ── Pure helpers ──────────────────────────────────────────────────────────────

const IMAGE_PLACEHOLDER = /\{\{IMAGE:(\d+)\}\}/g;

function parseSegments(body: string): Segment[] {
    const parts = body.split(IMAGE_PLACEHOLDER);
    // parts alternates: text, id, text, id, text …
    const segments: Segment[] = [];
    for (let i = 0; i < parts.length; i++) {
        if (i % 2 === 0) {
            segments.push({ kind: "text", value: parts[i] });
        } else {
            segments.push({ kind: "image", id: Number(parts[i]) });
        }
    }
    // Always end with a text segment so there's always an editable area
    if (segments.length === 0 || segments[segments.length - 1].kind === "image") {
        segments.push({ kind: "text", value: "" });
    }
    return segments;
}

function buildBody(segments: Segment[]): string {
    return segments
        .map((s) => (s.kind === "text" ? s.value : `{{IMAGE:${s.id}}}`))
        .join("");
}

function imageIdsOf(segments: Segment[]): number[] {
    return segments.filter((s): s is ImageSegment => s.kind === "image").map((s) => s.id);
}

// ── Public handle exposed via ref ─────────────────────────────────────────────

export interface CommunicationBodyEditorHandle {
    insertAtCursor: (placeholder: string) => void;
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
    body: string;
    onChange: (newBody: string) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Parses the text and uploaded images ands shows a clear and understandable text box.
 */
export const CommunicationBodyEditor = forwardRef<CommunicationBodyEditorHandle, Props>(
    function CommunicationBodyEditor({ body, onChange }, ref) {
        const [segments, setSegments] = useState<Segment[]>(() => parseSegments(body));
        const [blobUrls, setBlobUrls] = useState<Map<number, string>>(new Map());

        // Track which text-segment textarea is focused and the cursor position
        const focusedSegIdx = useRef<number>(0);
        const focusedCursor = useRef<number>(0);
        // Refs to AntD TextArea instances per text-segment index
        const textAreaRefs = useRef<Map<number, any>>(new Map());

        // ── Sync segments when external body changes (e.g. load on edit) ──────
        useEffect(() => {
            const next = parseSegments(body);
            setSegments(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [body]);

        // ── Fetch / revoke blob URLs for image segments ───────────────────────
        useEffect(() => {
            const ids = imageIdsOf(segments);
            if (ids.length === 0) {
                setBlobUrls(new Map());
                return;
            }

            let cancelled = false;
            const newUrls: Map<number, string> = new Map();

            Promise.all(
                ids.map(async (id) => {
                    const url = await CommunicationHttpService.getImageBlobUrl(id);
                    newUrls.set(id, url);
                }),
            ).then(() => {
                if (!cancelled) setBlobUrls(new Map(newUrls));
            });

            return () => {
                cancelled = true;
                newUrls.forEach((u) => URL.revokeObjectURL(u));
            };
        // Stringify IDs so effect only re-runs when the set of IDs actually changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [imageIdsOf(segments).join(",")]);

        // ── Expose insertAtCursor to parent ───────────────────────────────────
        useImperativeHandle(ref, () => ({
            insertAtCursor(placeholder: string) {
                // Extract image id from placeholder, e.g. "{{IMAGE:42}}" → 42
                const match = placeholder.match(/\{\{IMAGE:(\d+)\}\}/);
                if (!match) return;
                const imageId = Number(match[1]);

                setSegments((prev) => {
                    // Find the focused text segment (or last text segment as fallback)
                    let targetIdx = focusedSegIdx.current;
                    if (targetIdx < 0 || targetIdx >= prev.length || prev[targetIdx].kind !== "text") {
                        targetIdx = [...prev].reverse().findIndex((s) => s.kind === "text");
                        if (targetIdx === -1) targetIdx = 0;
                        else targetIdx = prev.length - 1 - targetIdx;
                    }

                    const seg = prev[targetIdx] as TextSegment;
                    const cursor = focusedCursor.current;
                    const before = seg.value.substring(0, cursor);
                    const after = seg.value.substring(cursor);

                    const next: Segment[] = [
                        ...prev.slice(0, targetIdx),
                        { kind: "text", value: before },
                        { kind: "image", id: imageId },
                        { kind: "text", value: after },
                        ...prev.slice(targetIdx + 1),
                    ];

                    onChange(buildBody(next));
                    return next;
                });
            },
        }));

        // ── Text segment change ───────────────────────────────────────────────
        function handleTextChange(segIdx: number, newValue: string) {
            setSegments((prev) => {
                const next = prev.map((s, i) =>
                    i === segIdx ? { kind: "text" as const, value: newValue } : s,
                );
                onChange(buildBody(next));
                return next;
            });
        }

        // ── Image delete ──────────────────────────────────────────────────────
        function handleDeleteImage(id: number, segIdx: number) {
            // Revoke blob URL immediately
            const url = blobUrls.get(id);
            if (url) URL.revokeObjectURL(url);

            setSegments((prev) => {
                // Remove the image segment; merge surrounding text segments
                const next: Segment[] = [];
                for (let i = 0; i < prev.length; i++) {
                    if (i === segIdx) continue; // skip the deleted image
                    const last = next[next.length - 1];
                    if (last && last.kind === "text" && prev[i].kind === "text") {
                        // Merge adjacent text segments
                        next[next.length - 1] = {
                            kind: "text",
                            value: last.value + (prev[i] as TextSegment).value,
                        };
                    } else {
                        next.push(prev[i]);
                    }
                }
                // Ensure at least one text segment
                if (next.length === 0 || next[next.length - 1].kind === "image") {
                    next.push({ kind: "text", value: "" });
                }
                onChange(buildBody(next));
                return next;
            });
        }

        // ── Render ────────────────────────────────────────────────────────────
        let textSegIdx = 0; // running index among text segments for ref tracking

        return (
            <div className="cbody-editor">
                {segments.map((seg, i) => {
                    if (seg.kind === "text") {
                        const myTextIdx = textSegIdx++;
                        const mySegIdx = i;
                        return (
                            <Input.TextArea
                                key={`text-${i}`}
                                ref={(r) => {
                                    if (r) textAreaRefs.current.set(myTextIdx, r);
                                    else textAreaRefs.current.delete(myTextIdx);
                                }}
                                variant="borderless"
                                placeholder={
                                    segments.length === 1
                                        ? "Testo del messaggio"
                                        : undefined
                                }
                                value={seg.value}
                                autoSize={{ minRows: 1 }}
                                className="cbody-text-segment"
                                onChange={(e) => handleTextChange(mySegIdx, e.target.value)}
                                onFocus={(e) => {
                                    focusedSegIdx.current = mySegIdx;
                                    focusedCursor.current =
                                        e.target.selectionStart ?? seg.value.length;
                                }}
                                onSelect={(e) => {
                                    focusedSegIdx.current = mySegIdx;
                                    focusedCursor.current =
                                        (e.target as HTMLTextAreaElement).selectionStart ??
                                        seg.value.length;
                                }}
                                onKeyUp={(e) => {
                                    focusedCursor.current =
                                        (e.target as HTMLTextAreaElement).selectionStart ??
                                        seg.value.length;
                                }}
                            />
                        );
                    }

                    // Image segment
                    const blobUrl = blobUrls.get(seg.id);

                    return (
                        <div key={`img-${seg.id}`} className="cbody-img-wrap">
                            {blobUrl ? (
                                <img
                                    src={blobUrl}
                                    alt=""
                                    className="cbody-img"
                                    style={{ maxWidth: "100%", display: "block" }}
                                />
                            ) : (
                                <Spin size="small" />
                            )}
                            <button
                                className="cbody-img-delete"
                                title="Rimuovi immagine"
                                onClick={() => handleDeleteImage(seg.id, i)}
                            >
                                ×
                            </button>
                        </div>
                    );
                })}
            </div>
        );
    },
);
